import Box, { BoxProps } from '@material-ui/core/Box';
import Collapse, { CollapseProps } from '@material-ui/core/Collapse';
import Fade, { FadeProps } from '@material-ui/core/Fade';
import Grid, { GridProps } from '@material-ui/core/Grid';
// TODO: fix this since withWidth was completely removed from MUI in v5: https://next.material-ui.com/guides/migration-v4/
// import withWidth, { WithWidthProps } from '@material-ui/core/withWidth';
import CheckboxWithLabel from 'components/CheckboxWithLabel';
import { HintsProps } from 'components/Hints';
import { FieldInputProps } from 'formik';
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import styled from 'styled-components';

type ContextType = {
  checked: boolean;
  hasChecked: boolean;
};
export type Props = {
  editable: boolean;
  text: string | ReactNode;
  orientation: 'row' | 'column';
  children: ReactNode;
  name: string;
  labelBold?: boolean;
  field: FieldInputProps<any>;
  suffix?: ReactElement;
  optionalProps?: BoxProps;
  onChange?: (e: React.ChangeEvent) => void;
  wrapperProps?: BoxProps;
  firstGridProps?: GridProps;
  secondGridProps?: GridProps;
  endAdorment?: ReactElement;
  fitContent?: boolean;
  form: any;
} & HintsProps;

const Context = createContext({} as ContextType);
const CheckBox = styled(CheckboxWithLabel)`
  align-self: center;
`;

const BgColor = styled(Box)`
  background: ${(props) => props.theme.colors.aliceBlue04};
  border-radius: 8px;
`;

const CheckboxWithOptionalContent: React.VFC<Props & BoxProps> = ({
  editable,
  text,
  orientation = 'row',
  children,
  labelBold,
  onChange,
  field,
  suffix,
  wrapperProps,
  optionalProps,
  firstGridProps,
  secondGridProps,
  width,
  endAdorment,
  form,
  fitContent,
}) => {
  const [hasChecked, setHasChecked] = useState(false);
  const AnimationForShow = useCallback(
    (props: FadeProps & CollapseProps): ReactElement => {
      if (orientation === 'row' && width !== 'xs') {
        return <Fade {...props} />;
      }
      return <Collapse {...props} />;
    },
    [orientation, width],
  );

  return (
    <Box
      sx={{
        py: 1.25,
        ...wrapperProps,
      }}
    >
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12} sm={orientation === 'column' ? 12 : 4} {...firstGridProps}>
          <CheckBox
            label={text}
            editable={editable}
            field={{
              ...field,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                setHasChecked(true);
                onChange && onChange(e);
              },
            }}
            labelBold={labelBold}
            suffix={suffix}
            form={form}
          />
        </Grid>
        <Grid item xs={12} sm={orientation === 'column' ? 12 : 8} {...secondGridProps}>
          <Context.Provider value={{ checked: field.value, hasChecked }}>
            <AnimationForShow in={!!field.value}>
              <BgColor
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  mt: orientation === 'column' ? 1 : 0,
                  position: 'relative',
                  width: fitContent ? 'fit-content' : 'auto',
                  p: 1,
                  ...optionalProps,
                }}
              >
                {children}
                {endAdorment}
              </BgColor>
            </AnimationForShow>
          </Context.Provider>
        </Grid>
      </Grid>
    </Box>
  );
};

export const useContextCheckboxWithOptionalContent = () => {
  return useContext(Context);
};

export default CheckboxWithOptionalContent;
